import {
  AUDIENCE_MODE,
  CAMPAIGN_STATUS,
  CHANNEL_RELATIONSHIP_SCOPE,
  emailApi,
  EMAIL_TEMPLATE_KEY,
  getToken
} from 'lib';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation, useOutletContext, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Card, Empty, Failed, T } from './components/ui';

/**
 * Email Manager — the shell shared by its five pages.
 *
 * ## The arrangement the design asks for
 *
 * The board's frames navigate each other, and the navigation is the design: the manager opens on
 * the **audience**, its header's `View Templates` opens the **catalog**, and `Create Campaign`
 * opens **Setup & Automation** — one frame carrying the template editor, the campaign builder and
 * the scheduler under a single header.
 *
 *   /admin/email            Dashboard    who we can reach     the audience, and every campaign
 *   /admin/email/templates  Templates    what we can send     the nine-card catalog
 *   /admin/email/setup      Setup        write one and send   editor + builder + scheduler
 *   /admin/email/reports    Reports      how it did           stats, send lookup
 *   /admin/email/settings   Settings     the standing rules   policy + compliance footer
 *
 * This reverses an earlier split that gave the editor, the builder and the scheduler a route each.
 * The reasoning then was that each frame repeats the logo, a title and a Settings button, so the
 * repetition must mean "separate pages". It reads the other way round: the frames repeat a header
 * because they are one scrolling page, and the three cards are one job — the template you just
 * edited is the one you are about to send, and splitting them meant choosing it twice, in two
 * places, with a route boundary in between.
 *
 * What did not merge: the catalog, the analytics and the standing rules each answer a question on
 * their own, and the header's own `Settings` button only means anything if Settings is elsewhere.
 *
 * Nested routes with an `Outlet`, rather than independent screens, for one reason that matters: the
 * campaign builder stores **the audience filters**, and the editor and the builder share **the
 * selected template**. Both have to survive navigation. Separate screens would each hold their own
 * copy and give an operator two sources of truth for who gets the email and which template it uses.
 *
 * This shell therefore owns every fetch and every action, and the pages are layout. It also means
 * moving between pages does not re-run the audience aggregation, which is a real query over the
 * user collection rather than a cheap read.
 *
 * Deliberately absent throughout: any drag-and-drop builder, any HTML editing, any image upload.
 * Those are non-requirements in the brief, and the MJML component library is what keeps every email
 * consistent — a builder would immediately undo that.
 */

/**
 * A redirect that keeps the query string.
 *
 * `audience` and `compose` were pages until the merge and are linked from outside the app, and
 * those links carry the state: `?campaign=` opens one campaign, `?filters=` carries an audience,
 * `?template=` chooses a template. A bare `<Navigate to="/admin/email/setup" />` drops all three,
 * so a bookmarked campaign link would land on an empty composer — which reads as the campaign
 * having been lost.
 */
export const EmailRedirect = ({ to }) => {
  const { search } = useLocation();

  return <Navigate to={`${to}${search}`} replace />;
};

const Shell = styled.div`
  background: ${T.page};
  min-height: 100%;
  padding: 22px 30px 60px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

/*
  There is no page switcher.

  A quiet strip of five tabs sat here, added because the design board gives no route to its
  Analytics frame at all and a page an operator cannot reach is not a page. It has been removed, and
  the reachability argument it rested on has not gone away — it has been paid off in the headers
  instead, which is where the design does its own navigating.

  Every route is now reached by a button that says what it is for rather than what it is called:
  `View Templates` for the catalog, `Create Campaign` for setup, `Reports` for the analytics, the
  `Settings` gear from anywhere, and `Back to dashboard` from each of the four. `index.test.js`
  walks that graph rather than trusting it, because a strip of links is hard to break by accident
  and a graph of buttons is not.
*/

/** Every page reads the shell's data and actions through this. */
export const useEmailManager = () => useOutletContext();

/** Challenges per request: the list endpoint's own maximum. */
const CHALLENGE_PAGE_SIZE = 50;
/** A ceiling on pages per load, 1,000 challenges, so a runaway list cannot hold the composer. */
const CHALLENGE_PAGES_MAX = 20;

const EmailManager = () => {
  const user = useSelector((state) => state.user);
  const isAdmin = !!user?.admin;

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedKey = searchParams.get('template') || EMAIL_TEMPLATE_KEY.CAMPAIGN;
  const previewVariant = searchParams.get('variant') || 'desktop';
  const showVersions = searchParams.get('versions') === '1';

  const updateParams = useCallback(
    (updates) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([key, value]) => {
            if (value === null || value === undefined || value === '') next.delete(key);
            else next.set(key, value);
          });
          return next;
        },
        /*
          A push, not a replace.

          Replacing meant entering a template's detail view overwrote the history entry it came
          from, so browser Back left the Email Manager entirely rather than returning to the
          catalog. The parameters here are navigation — which template, which variant, which
          campaign — and navigation belongs in history.
        */
        { replace: false }
      );
    },
    [setSearchParams]
  );

  const [templates, setTemplates] = useState([]);
  const [templatesLoaded, setTemplatesLoaded] = useState(false);

  /**
   * Whether the template on screen is the one a campaign can be built on.
   *
   * One of the nine is. The other eight are fired by an account or billing event — a welcome, a
   * verification, a password reset — and have no audience, no schedule, no detail card and no
   * per-campaign destination. The setup page showed all of that for every template, because the
   * page was written for the one template that has it and the route takes a `?template=` for any
   * of the nine.
   *
   * Read from the catalog rather than from the loaded template detail, so it is settled on the
   * first render after the list arrives instead of one fetch later — the interval in between is
   * exactly long enough to paint a Campaign Builder over a password-reset email and then remove
   * it.
   *
   * Defaults to true while the list is still loading, for the same reason: `campaign` is the
   * route's default template, and flashing the campaign controls off and back on for the campaign
   * template is the more visible of the two wrong answers.
   */
  const isCampaignTemplate = useMemo(() => {
    const entry = templates.find((template) => template.key === selectedKey);

    return entry ? entry.campaignEligible !== false : true;
  }, [templates, selectedKey]);

  /*
    Whether each fetch succeeded, because the API client cannot tell us.

    `lib/api.js` never rejects: it returns `false` on failure, or `{ err }` when called
    `{ silent: true }`. So `res?.data || []` collapses "this failed" and "there is nothing" into
    the same value, and every screen here did exactly that. The consequences were not cosmetic —
    a failed template list told the operator their *database* was unseeded and handed them a shell
    command, and a failed policy fetch left Settings reading "Loading policy…" for ever.

    `false` means failed, `null` means not attempted yet, `true` means loaded. A boolean would not
    have been enough: "not yet" and "failed" are the two states that were being conflated.
  */
  const [loadOk, setLoadOk] = useState({});

  const markLoad = useCallback(
    (key, ok) => setLoadOk((prev) => (prev[key] === ok ? prev : { ...prev, [key]: ok })),
    []
  );

  /** True when a fetch definitely failed, as opposed to having returned nothing. */
  const failed = useCallback((key) => loadOk[key] === false, [loadOk]);
  const [template, setTemplate] = useState(null);
  const [blocks, setBlocks] = useState({});
  /**
   * Which template the copy in `blocks` belongs to.
   *
   * The shell outlives a navigation between `/templates` and `/setup`, so `blocks` survives it too
   * — holding the words of the template being left. Everything downstream that reads `blocks` has
   * to know whether they are about the template now on screen, and `selectedKey` cannot answer
   * that: it changes the instant the URL does, while `blocks` changes a fetch later.
   *
   * `null` until the first template lands, which reads as "these belong to nothing".
   */
  const [blocksKey, setBlocksKey] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [versions, setVersions] = useState({ versions: [], nextCursor: null });

  /*
    Audience filters live in the address bar, not only in memory.

    They were state alone, and the Audience page navigated to Compose with no query string — so a
    reload or a second tab dropped them, the composer then reported "No filters — every eligible
    fan", and Save Draft stored a campaign addressed to the whole audience. An operator who had
    narrowed to a few hundred fans was one click from twelve thousand, and nothing warned.

    Serialized as JSON in one parameter rather than spread across many: the filter set contains
    five arrays and two date ranges, and one opaque parameter round-trips them without inventing a
    second encoding for each shape. It also makes the composer's "were filters ever set?" question
    answerable — an absent parameter is unset, which is different from an empty object.
  */
  const filtersParam = searchParams.get('filters');

  const filters = useMemo(() => {
    if (!filtersParam) return {};

    try {
      return JSON.parse(filtersParam);
    } catch {
      // A hand-edited or truncated URL. Treated as unset rather than as "everyone" — see the
      // composer's refusal.
      return {};
    }
  }, [filtersParam]);

  /** True when an audience was actually chosen, as opposed to never set. */
  const filtersWereSet = Boolean(filtersParam);

  const setFilters = useCallback(
    (next) => {
      const value = typeof next === 'function' ? next(filters) : next;
      const hasAny =
        value &&
        Object.values(value).some((entry) =>
          Array.isArray(entry) ? entry.length > 0 : Boolean(entry)
        );

      updateParams({ filters: hasAny ? JSON.stringify(value) : null });
    },
    [filters, updateParams]
  );
  /*
    Which of the two audiences this campaign uses, in the address bar beside the filters.

    The same decision `filters` documents above, for the same reason: a reload or a second tab that
    dropped the mode would show a channel campaign as an unfiltered saved-segment one, and Save
    Draft would store a campaign addressed to the whole platform. Two short parameters rather than
    one JSON blob, because a channel audience is an id and an enum — there is no shape here that
    needs encoding.
  */
  /*
    Read through an allowlist rather than taken as written.

    An unknown `?audienceMode=` — a typo, an old link, a mode removed in a later release — must
    read as the saved-segment default and not as a mode the screen has no controls for. The
    alternative is a builder rendering neither picker while the save payload claims a criteria mode
    the server would then refuse.
  */
  const requestedMode = searchParams.get('audienceMode');
  const audienceMode =
    requestedMode === AUDIENCE_MODE.CHANNEL_SPECIFIC ||
    requestedMode === AUDIENCE_MODE.DIRECT_RECIPIENT
      ? requestedMode
      : AUDIENCE_MODE.SAVED_SEGMENT;

  const channelId = searchParams.get('channel') || '';
  const relationshipScope = searchParams.get('scope') || CHANNEL_RELATIONSHIP_SCOPE.FOLLOWERS;

  /** The account a single-recipient campaign names, in the address bar beside the channel. */
  const recipientId = searchParams.get('recipient') || '';

  /**
   * The channel's name, held in state rather than in the URL.
   *
   * It is a display snapshot and the server owns it — every count and every save answers with the
   * current one. Putting it in the address bar would make a stale link able to claim a campaign is
   * aimed at a channel it is not.
   */
  const [channelLabel, setChannelLabel] = useState('');

  /**
   * The recipient's name, held in state rather than in the URL, for the reason `channelLabel` is:
   * it is a display snapshot the server owns, and a stale link able to claim a campaign is aimed
   * at somebody it is not would be worse here than anywhere else on this screen.
   */
  const [recipientLabel, setRecipientLabel] = useState('');

  /** What is typed into the recipient picker's search box. Not a URL parameter: it is a way of
   * finding the account, not part of the campaign. */
  const [recipientSearch, setRecipientSearch] = useState('');

  const setAudienceMode = useCallback(
    (mode) => {
      /*
        Switching modes clears the other mode's criteria (FR-013, FR-016).

        Leaving them behind is what makes "which audience is this campaign actually using" a
        question an operator has to work out rather than read. The server does the same thing on
        save; doing it here too means the screen never shows an audience the save would discard.
      */
      if (mode === AUDIENCE_MODE.CHANNEL_SPECIFIC) {
        setRecipientLabel('');
        updateParams({ audienceMode: mode, filters: null, recipient: null });
        return;
      }

      if (mode === AUDIENCE_MODE.DIRECT_RECIPIENT) {
        setChannelLabel('');
        updateParams({ audienceMode: mode, filters: null, channel: null, scope: null });
        return;
      }

      setChannelLabel('');
      setRecipientLabel('');
      updateParams({ audienceMode: null, channel: null, scope: null, recipient: null });
    },
    [updateParams]
  );

  const setChannelAudience = useCallback(
    (channel, scope) => {
      setChannelLabel(channel?.label || '');
      updateParams({
        audienceMode: AUDIENCE_MODE.CHANNEL_SPECIFIC,
        channel: channel?.id || null,
        scope: scope || relationshipScope,
        filters: null
      });
    },
    [relationshipScope, updateParams]
  );

  const setDirectRecipient = useCallback(
    (recipient) => {
      setRecipientLabel(recipient?.label || '');
      updateParams({
        audienceMode: AUDIENCE_MODE.DIRECT_RECIPIENT,
        recipient: recipient?.id || null,
        filters: null,
        channel: null,
        scope: null
      });
    },
    [updateParams]
  );

  /**
   * The channel audience's own count, and why it cannot send when it cannot.
   *
   * Separate state from `audience`, which is the filter path's page of rows and its estimated
   * match count. Merging them would make one object mean two different things depending on a mode
   * held somewhere else — and the two numbers genuinely differ: this one is final, and the send
   * controls block on it.
   *
   * `status` starts as `idle` and is never `null`-as-zero: "not counted yet" and "counted, and the
   * answer is nobody" must not look the same to the controls that read this.
   */
  const [channelAudience, setChannelAudienceState] = useState({
    status: 'idle',
    eligibleRecipients: null,
    canSend: false,
    blockReason: null,
    label: ''
  });

  /**
   * The single recipient's own eligibility, and why they cannot be sent to when they cannot.
   *
   * Its own state rather than a shape shared with `channelAudience`, for the reason that one is
   * separate from `audience`: one object meaning different things depending on a mode held
   * somewhere else is how a screen comes to show a number for an audience nobody selected. It
   * carries one field the channel's does not — `recipientEmail`, the live address — because that
   * is the fact this mode exists to let an operator confirm.
   */
  const [directRecipient, setDirectRecipientState] = useState({
    status: 'idle',
    eligibleRecipients: null,
    canSend: false,
    blockReason: null,
    recipientEmail: '',
    label: ''
  });

  const [audience, setAudience] = useState({
    rows: [],
    total: 0,
    audienceTotal: 0,
    nextCursor: null
  });
  const [audienceLoading, setAudienceLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  /** Rendered beside the export button: a failed export used to be completely silent. */
  const [exportError, setExportError] = useState(null);
  const [events, setEvents] = useState([]);

  const [preview, setPreview] = useState({});
  const [previewLoading, setPreviewLoading] = useState(false);
  /*
    How many times a preview width has been *asked for*, as opposed to which width is current.

    `?variant=` carries the width so it survives a reload and a shared link. It cannot carry the
    asking: pressing **Preview Mobile** twice sets the same parameter twice, and an operator who
    toggled the pane back to Desktop in between gets nothing for their second press. So the count
    lives here, in memory, and increments on every press — the pane adopts the width whenever this
    changes. Memory-only on purpose: a request is something that just happened, and opening a
    shared link must not replay it.
  */
  const [previewVariantRequest, setPreviewVariantRequest] = useState(0);
  /*
    Which language's banner the preview draws.

    Shell view-state, deliberately not part of the campaign draft. The campaign has no language and
    must not acquire one by accident: switching this changes what an operator is looking at and
    nothing about who receives what, so it is held here beside the width toggle rather than
    anywhere `saveCampaign` can reach.
  */
  const [previewLocale, setPreviewLocale] = useState('en');
  const [previewLocales, setPreviewLocales] = useState([]);

  /*
    The campaign template's event-block values, carried rather than composed.

    The builder used to collect these in two text fields. The detail rows below say the same things
    in the operator's own words, so the fields are gone — but campaigns saved before that still hold
    an event date and a next action, and a saved row pointing at `{Event Date}` still resolves from
    this. So they are loaded and written back untouched, which keeps an old campaign rendering what
    it always rendered instead of quietly losing it on the next save.
  */
  const [eventDate, setEventDate] = useState('');
  const [nextAction, setNextAction] = useState('');
  /*
    The detail card's rows, which are campaign data in the same sense the two fields above are.

    Empty array until a campaign is loaded or created, and the server is what decides what a new
    campaign starts with — the four rows the template has always rendered. The client does not carry
    its own copy of that list: two copies of "what a campaign starts with" is one of them being
    wrong after the first change to either.
  */
  const [detailRows, setDetailRows] = useState([]);
  /*
    Rows changed since the last save, which is not the same thing as `dirty`.

    `dirty` means unsaved *template text*: it draws the "Unsaved changes" badge on the Template
    Editor card and is cleared by the template load effect. A campaign's rows are neither, so
    borrowing it would put that badge on the wrong card and let a template reload silently decide a
    campaign had been saved. This is what the preview settle and the unload warning read instead.
  */
  const [rowsDirty, setRowsDirty] = useState(false);

  const changeDetailRows = useCallback((rows) => {
    setDetailRows(rows);
    setRowsDirty(true);
  }, []);

  const [conversionAction, setConversionAction] = useState('');
  const [destination, setDestination] = useState('');
  const [destinationTarget, setDestinationTarget] = useState('');

  /*
    The FanCam challenges the button may point at, and what happened to the one already chosen.

    `challengeResolved` is three states, not two. `undefined` means nobody has asked yet; a row means
    the stored challenge exists; `null` means it was asked for and is gone. Only the third may block
    a send, and collapsing it into "falsy" would block every campaign during the moment before the
    lookup returns.
  */
  const [challenges, setChallenges] = useState([]);
  const [challengesLoading, setChallengesLoading] = useState(false);
  const [challengesHasMore, setChallengesHasMore] = useState(false);
  const [challengeResolved, setChallengeResolved] = useState(undefined);
  const [campaigns, setCampaigns] = useState([]);

  /*
    Always asks for archived rows.

    The endpoint applies `archivedAt: null` unless told otherwise, and nothing here ever told it —
    so the Archived segment could never contain a row and the Restore button beside each archived
    campaign was unreachable. Archiving worked; un-archiving did not.

    Occurrences of a repeating campaign need no flag: the endpoint only filters on
    `recurringParent` when asked to, so they are already in an unfiltered list, which is what the
    list's occurrence count reads.

    One helper rather than a parameter at six call sites, so this cannot drift apart again.
  */
  const loadCampaigns = useCallback(async () => {
    const res = await emailApi.listCampaigns({ includeArchived: true });
    markLoad('campaigns', res !== false);
    setCampaigns(res?.data || []);
  }, [markLoad]);
  const [campaignName, setCampaignName] = useState('');
  const [campaignId, setCampaignId] = useState(null);

  /*
    Why the rows cannot be edited, or null when they can.

    A campaign stops being editable the moment it is no longer a draft, and `updateCampaign` has
    always refused on that basis. Saying so beside the fields is the difference between a control
    that looks broken and one that explains itself — the operator's next move is to cancel the
    campaign or duplicate it, which is what the endpoint's own refusal tells them.

    Below the two it reads, and that placement is load-bearing rather than tidy: a `const` read
    above its own declaration is a temporal dead zone error, which crashes this screen on mount and
    which neither the build nor the type checker can see.
  */
  const rowsReadOnlyReason = useMemo(() => {
    if (!campaignId) return null;

    const campaign = campaigns.find((row) => (row.id || row._id) === campaignId);
    if (!campaign || campaign.status === 'draft') return null;

    return `This campaign is ${campaign.status}. Cancel it or duplicate it to change its rows.`;
  }, [campaignId, campaigns]);

  /*
    Adopting a campaign chosen on the dashboard.

    The dashboard's "Open" is a navigation, so the campaign it means arrives as `?campaign=<id>`.
    Without this the operator would land on a page that had forgotten which campaign they clicked —
    the link would look like it worked and would have opened nothing.

    Its name and filters are taken from the row too, so the builder shows what the operator was
    looking at a moment ago rather than an empty form. Runs when the id changes or the list loads,
    whichever is later; the list is fetched asynchronously and the param can arrive first.
  */
  const openedCampaignId = searchParams.get('campaign');

  useEffect(() => {
    if (!openedCampaignId || campaigns.length === 0) return;

    const campaign = campaigns.find((row) => (row.id || row._id) === openedCampaignId);
    if (!campaign) return;

    setCampaignId(openedCampaignId);
    setCampaignName(campaign.name || '');
    /*
      The audience mode travels with the filters, and for a channel campaign instead of them.

      A campaign saved against a channel stores an empty filter set, so restoring only `filters`
      would reopen it with no audience at all — the operator would watch their channel disappear,
      and saving would write that loss back. The same defect the composer's other fields were
      added to fix.
    */
    if (campaign.audienceMode === AUDIENCE_MODE.CHANNEL_SPECIFIC && campaign.channelAudience) {
      setChannelLabel(campaign.channelAudience.channelLabel || '');
      updateParams({
        audienceMode: AUDIENCE_MODE.CHANNEL_SPECIFIC,
        channel: campaign.channelAudience.channelId,
        scope: campaign.channelAudience.relationshipScope,
        filters: null,
        recipient: null
      });
    } else if (
      campaign.audienceMode === AUDIENCE_MODE.DIRECT_RECIPIENT &&
      campaign.directRecipient
    ) {
      /*
        The saved recipient, restored by id and name — and not by address.

        The row carries `recipientEmail` and it is deliberately not adopted into the picker's
        state: the address shown beside the send controls is re-read from the server alongside the
        count, so what an operator confirms before pressing send is where the campaign will
        actually go rather than where it would have gone when the draft was saved.
      */
      setRecipientLabel(campaign.directRecipient.recipientLabel || '');
      updateParams({
        audienceMode: AUDIENCE_MODE.DIRECT_RECIPIENT,
        recipient: campaign.directRecipient.userId,
        filters: null,
        channel: null,
        scope: null
      });
    } else if (campaign.filters) {
      setFilters(campaign.filters);
    }
    // Adopted alongside the name and filters, so reopening a campaign shows what it will send.
    setEventDate(campaign.eventDate || '');
    setNextAction(campaign.nextAction || '');
    setDetailRows(campaign.detailRows || []);
    setRowsDirty(false);
    setConversionAction(campaign.conversionAction || '');
    setDestination(campaign.destination || '');
    setDestinationTarget(campaign.destinationTarget || '');
  }, [openedCampaignId, campaigns, setFilters, updateParams]);

  /** The stop-window a queued send leaves behind, so the screen can show and act on it. */
  const [sendWindow, setSendWindow] = useState(null);
  /*
    And on a real page unload, where a React confirm cannot run. The browser shows its own prompt;
    the message is ignored by every modern browser but the presence of a handler is what triggers
    it.
  */
  useEffect(() => {
    if (!dirty && !rowsDirty) return undefined;

    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, rowsDirty]);

  const [policy, setPolicy] = useState(null);

  /*
    A named loader, so the retry on the Settings panel re-runs the real request.

    It was inline in the bootstrap effect, which meant a failed policy fetch had no second attempt
    short of reloading the page — and the panel could not offer one.
  */
  const loadPolicy = useCallback(async () => {
    const res = await emailApi.getFatiguePolicy();
    markLoad('policy', res !== false);
    setPolicy(res?.data || null);
  }, [markLoad]);

  /*
    One name for the action in flight, rather than one shared boolean.

    Every action set the same `busy` flag, and every control in the screen read it — so pressing
    Test Send made the button beside it read "Scheduling..." and left "Save Policy" on another page
    reading "Saving…". Fourteen actions were reporting each other's progress.

    `busy` is kept, derived, because disabling *all* the write controls while any one of them is in
    flight is correct: they act on the same campaign. It is only the *label* that has to know which
    action is running, and that is what `isRunning` answers.
  */
  const [running, setRunning] = useState(null);
  const busy = running !== null;

  /** True while this specific action is in flight — for the label on the button that started it. */
  const isRunning = useCallback((action) => running === action, [running]);

  /*
    What the last action did, in the operator's words.

    Duplicate, Archive and Save Policy all completed silently. Duplicate and Archive do refresh the
    campaign list — but that list is on the Dashboard and the buttons are in the composer, so from
    where the operator was standing nothing happened at all: the label stopped saying "Duplicating…"
    and that was the entire feedback. Saving the policy gave even less; the response was stored and
    nothing on the panel changed.

    Keyed by action, not a single message, so a confirmation appears under the control that earned
    it — the Settings page has two panels writing through one function, and an unkeyed message put
    the footer's confirmation under the policy panel.

    Cleared by `begin`, so a confirmation cannot outlive the state it describes.
  */
  const [done, setDone] = useState(null);

  /** Starts an action: names it, and drops the previous action's confirmation. */
  const begin = useCallback((action) => {
    setRunning(action);
    setDone(null);
    setRefusals((prev) => (prev[action] ? { ...prev, [action]: null } : prev));
  }, []);

  /** The confirmation for this action, if it is the one that last completed. */
  const confirmation = useCallback(
    (action) => (done?.action === action ? done.message : null),
    [done]
  );

  /*
    A server refusal, kept and rendered rather than toasted.

    Every campaign write is `{ silent: true }` now, so the wrapper hands back `{ err, status }`
    instead of dispatching a six-second red toast into the corner of the window. The refusals worth
    reading all name a cause the operator can act on — the campaign is no longer a draft and its
    status is named, the time is in the past, no postal address is stored — and a toast put that
    text at the wrong end of the page from the control that caused it, then took it away.

    `read` turns any wrapper outcome into either the message or `null` for success. `false` is the
    wrapper's own signal for a failure it declined to describe, and a 500 arrives as `{ status }`
    with no `err`, so both fall back to a sentence rather than rendering "undefined".
  */
  const [refusals, setRefusals] = useState({});

  const refusalOf = useCallback((res) => {
    if (res === false) return 'The request did not complete. Nothing was changed.';

    /*
      `err` first, then the status.

      Every refusal the backend writes carries `err` (see `ApiResponse.error`), and a 401 is the
      case that makes the order matter: the wrapper returns the response body for a 401 *without*
      a `status`, so a predicate keyed on the status alone read an expired session as a successful
      save. A success payload is `{ data, pagination, msg }` and carries neither.
    */
    if (res?.err) return res.err;
    if (res?.status >= 400) return `The server refused this (${res.status}). Nothing was changed.`;
    return null;
  }, []);

  /** Records a refusal against the action that caused it, and reports whether there was one. */
  const refused = useCallback(
    (action, res) => {
      const message = refusalOf(res);
      setRefusals((prev) => ({ ...prev, [action]: message }));
      return !!message;
    },
    [refusalOf]
  );

  /** The refusal to render beside this action's control, if its last attempt was refused. */
  const refusal = useCallback((action) => refusals[action] || null, [refusals]);
  // Rendered inline beside the button rather than as a toast: the useful part of the hourly-limit
  // refusal is the reset time, and a toast is gone before an operator reads it.
  const [testSendResult, setTestSendResult] = useState(null);

  const [reportId, setReportId] = useState('');
  const [reportStats, setReportStats] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [lookup, setLookup] = useState('');
  const [sends, setSends] = useState({ sends: [], nextCursor: null });
  /** The address the currently-displayed result set belongs to, so a retyped box is not read as a result. */
  const [searchedFor, setSearchedFor] = useState(null);

  // --- Loading ---

  /*
    A named loader, so the catalog's retry re-runs the request.

    The banner's Try again called `window.location.reload()` — which does work, and throws away
    every other thing on the screen to do it: an unsaved draft, the filters, the open editor. The
    one request that failed is the one worth retrying.
  */
  const loadTemplates = useCallback(async () => {
    const res = await emailApi.listTemplates();
    // `res === false` is the client's way of reporting a failure. An empty array is a real,
    // successful, empty catalog — which is a different thing and says something different.
    markLoad('templates', res !== false);
    setTemplates(Array.isArray(res?.data) ? res.data : []);
    setTemplatesLoaded(true);
  }, [markLoad]);

  useEffect(() => {
    if (!isAdmin) return;

    loadTemplates();
    loadCampaigns();
    loadPolicy();
    // Options for the "took part in" filter. Loaded once and left empty on failure, which hides
    // that one control rather than failing the whole filter panel.
    emailApi
      .listEvents()
      .then(setEvents)
      .catch(() => setEvents([]));
  }, [isAdmin, loadCampaigns, loadPolicy, loadTemplates, markLoad]);

  useEffect(() => {
    if (!isAdmin || !selectedKey) return;

    emailApi.getTemplate(selectedKey).then((res) => {
      markLoad('template', res !== false);
      setTemplate(res?.data || null);
      setBlocks(res?.data?.blocks || {});
      /*
        Set with the blocks, never apart from them, and set even when the fetch failed.

        Together, because the pair is the claim "this copy is this template's" and half of it is
        worse than neither. Even on failure, because `setBlocks({})` above is itself an answer —
        the template has no copy on screen — and leaving the key behind would strand the preview
        waiting for blocks that are never coming.
      */
      setBlocksKey(selectedKey);
      setDirty(false);
    });
    emailApi
      .listTemplateVersions(selectedKey)
      .then((res) => setVersions(res?.data || { versions: [], nextCursor: null }));
  }, [isAdmin, selectedKey, markLoad]);

  /*
    The next page of template history.

    `VersionHistory` has always rendered an "Older versions" button when a cursor came back, and
    nothing ever passed it a handler — so `onClick` was `undefined` and everything past the first
    page was unreachable. The client and the endpoint both supported the cursor; only this was
    missing.

    Appends rather than replaces, because paging backwards through history should build a list
    rather than swap one page for another.
  */
  const loadOlderVersions = useCallback(async () => {
    if (!versions.nextCursor) return;

    const res = await emailApi.listTemplateVersions(selectedKey, versions.nextCursor);
    const page = res?.data;
    if (!page) return;

    setVersions((prev) => ({
      versions: [...prev.versions, ...(page.versions || [])],
      nextCursor: page.nextCursor || null
    }));
  }, [selectedKey, versions.nextCursor]);

  const loadAudience = useCallback(
    async (cursor) => {
      setAudienceLoading(true);
      const res = await emailApi.queryAudience(filters, 25, cursor);
      markLoad('audience', res !== false);
      const page = res?.data;

      setAudience((prev) =>
        cursor && page
          ? { ...page, rows: [...prev.rows, ...(page.rows || [])] }
          : page || { rows: [], total: 0, audienceTotal: 0, nextCursor: null }
      );
      setAudienceLoading(false);
    },
    [filters, markLoad]
  );

  useEffect(() => {
    if (!isAdmin) return undefined;
    // Debounced: the filter panel changes on every keystroke in a date field, and each change is a
    // real aggregation over the user collection.
    const timer = setTimeout(() => loadAudience(), 350);
    return () => clearTimeout(timer);
  }, [isAdmin, loadAudience]);

  /** The campaign-eligible channels the picker lists. */
  const [channels, setChannels] = useState([]);
  const [channelsLoading, setChannelsLoading] = useState(false);

  const loadChannels = useCallback(async () => {
    setChannelsLoading(true);
    const res = await emailApi.listCampaignChannels();
    // Silent endpoint: a refusal comes back as `{ err }` rather than throwing. An empty list is the
    // honest render for a search that failed — the picker says so beside it.
    setChannels(res?.data?.channels || []);
    setChannelsLoading(false);
  }, []);

  useEffect(() => {
    if (!isAdmin || audienceMode !== AUDIENCE_MODE.CHANNEL_SPECIFIC) return undefined;
    // Not debounced: with no search box this fires on switching into channel mode, which is one
    // deliberate click rather than a keystroke. One read of the platform's channel roster.
    loadChannels();
    return undefined;
  }, [isAdmin, audienceMode, loadChannels]);

  /*
    The channel audience's count.

    Re-run whenever the channel or the scope changes, because both change who receives the
    campaign. Not debounced: neither is typed, so each change is one deliberate click.

    The `idle` reset when no channel is chosen is load-bearing. Without it, deselecting a channel
    would leave the previous channel's count on screen and the send controls open — the operator
    would be looking at a number for an audience that is no longer selected.
  */
  const loadChannelAudience = useCallback(async () => {
    if (!channelId) {
      setChannelAudienceState({
        status: 'idle',
        eligibleRecipients: null,
        canSend: false,
        blockReason: null,
        label: ''
      });
      return;
    }

    setChannelAudienceState((prev) => ({ ...prev, status: 'loading' }));

    const res = await emailApi.previewChannelAudience({
      channelId,
      relationshipScope,
      channelLabel: channelLabel || undefined
    });
    const data = res?.data;

    if (!data) {
      /*
        The request itself failed, which is not the same as a count of zero.

        Reported as `countUnavailable` so the builder says the audience could not be verified and
        keeps the send controls shut, rather than showing a confident "0 fans" for a question
        nobody answered.
      */
      setChannelAudienceState({
        status: 'ready',
        eligibleRecipients: null,
        canSend: false,
        blockReason: 'countUnavailable',
        label: ''
      });
      return;
    }

    // The server's current name for the channel, so a rename shows up without a reload.
    if (data.channelLabel) setChannelLabel(data.channelLabel);

    setChannelAudienceState({
      status: 'ready',
      eligibleRecipients: data.eligibleRecipients,
      canSend: Boolean(data.canSend),
      blockReason: data.blockReason || null,
      label: data.label || ''
    });
  }, [channelId, relationshipScope, channelLabel]);

  useEffect(() => {
    if (!isAdmin || audienceMode !== AUDIENCE_MODE.CHANNEL_SPECIFIC) return;
    loadChannelAudience();
    /*
      `channelLabel` is deliberately absent from the dependency list.

      `loadChannelAudience` writes it from the response, so depending on it here would re-run the
      count every time the server returned a name — an infinite loop over a real aggregation. The
      two things that actually change the audience, the channel and the scope, are both here.
    */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, audienceMode, channelId, relationshipScope]);

  /** The accounts the single-recipient picker lists. */
  const [recipients, setRecipients] = useState([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);

  const loadRecipients = useCallback(async (search) => {
    setRecipientsLoading(true);
    const res = await emailApi.listCampaignRecipients(search || undefined);
    // Silent endpoint: a refusal comes back as `{ err }` rather than throwing, and an empty list is
    // the honest render — the picker says which kind of empty it is beside the control.
    setRecipients(res?.data?.recipients || []);
    setRecipientsLoading(false);
  }, []);

  useEffect(() => {
    if (!isAdmin || audienceMode !== AUDIENCE_MODE.DIRECT_RECIPIENT) return undefined;

    /*
      Debounced, where the channel list is not.

      This one is driven by a text box rather than by a mode switch, so without a settle every
      keystroke is a prefix query against the whole user collection — the exact shape the audience
      table is debounced for, at 350ms, and this matches it.
    */
    const timer = setTimeout(() => loadRecipients(recipientSearch), 350);
    return () => clearTimeout(timer);
  }, [isAdmin, audienceMode, recipientSearch, loadRecipients]);

  /*
    Whether the chosen account can actually be emailed.

    Re-run when the account changes, and only then: unlike the channel path there is no second
    criterion, and the search term deliberately does not appear here — typing in the box narrows
    the list, it does not change who the campaign is aimed at.

    The `idle` reset when nothing is chosen is load-bearing for the same reason it is there: a
    stale "ready to send to 1 recipient" left on screen after deselecting would hold the send
    controls open for an audience that no longer exists.
  */
  const loadDirectRecipient = useCallback(async () => {
    if (!recipientId) {
      setDirectRecipientState({
        status: 'idle',
        eligibleRecipients: null,
        canSend: false,
        blockReason: null,
        recipientEmail: '',
        label: ''
      });
      return;
    }

    setDirectRecipientState((prev) => ({ ...prev, status: 'loading' }));

    const res = await emailApi.previewDirectRecipient({
      userId: recipientId,
      recipientLabel: recipientLabel || undefined
    });
    const data = res?.data;

    if (!data) {
      // The request itself failed, which is not the same as an ineligible account — reported as
      // `countUnavailable` so the builder keeps the send controls shut rather than showing a
      // confident answer to a question nobody answered.
      setDirectRecipientState({
        status: 'ready',
        eligibleRecipients: null,
        canSend: false,
        blockReason: 'countUnavailable',
        recipientEmail: '',
        label: ''
      });
      return;
    }

    // The server's current name for the account, so a rename shows up without a reload.
    if (data.recipientLabel) setRecipientLabel(data.recipientLabel);

    setDirectRecipientState({
      status: 'ready',
      eligibleRecipients: data.eligibleRecipients,
      canSend: Boolean(data.canSend),
      blockReason: data.blockReason || null,
      recipientEmail: data.recipientEmail || '',
      label: data.label || ''
    });
  }, [recipientId, recipientLabel]);

  useEffect(() => {
    if (!isAdmin || audienceMode !== AUDIENCE_MODE.DIRECT_RECIPIENT) return;
    loadDirectRecipient();
    /*
      `recipientLabel` is deliberately absent, exactly as `channelLabel` is from the channel
      effect: `loadDirectRecipient` writes it from the response, so depending on it here would
      re-run the check every time the server returned a name.
    */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, audienceMode, recipientId]);

  /*
    Which preview request is the newest, so a slow one cannot land on top of a fast one.

    A ref rather than state: it changes on every request and nothing renders from it, so state would
    buy a re-render per keystroke for no visible difference.

    This mattered little while previews were pressed for one at a time. It matters now that typing
    starts them: two renders overlap the moment an operator types through a settle, and the server
    is free to answer the older one last. The symptom would be the preview snapping back to a word
    the operator had already deleted.
  */
  const previewRequestId = useRef(0);

  const refreshPreview = useCallback(async () => {
    if (!selectedKey) return;

    /*
      The copy on screen still belongs to the template we just left.

      `previewTemplate` spreads the caller's blocks *over* the template's own stored copy — the
      client wins, which is what lets an operator preview unsaved wording. So a render fired with
      the new key and the old blocks draws the new template's frame filled with the previous
      template's words, and that is exactly what opening any template from the catalog did: the
      shell survives the navigation, `blocks` survives with it, and the preview effect runs on the
      new `selectedKey` a fetch before the new copy arrives.

      Nothing corrected it afterwards, which is what made it look like caching: the settle that
      re-renders the pane only runs while the text is dirty, and loading a template sets it clean.
      A reload fixed it because `blocks` starts empty, and empty is what the endpoint reads as
      "use the template's own copy".

      Sent as `undefined` rather than skipped, and that is the whole difference between one render
      and none. Skipping until the copy arrives leaves the pane empty for a fetch — on first load
      it has nothing to show at all — where `undefined` is not a gap but an instruction: the
      endpoint reads it as "use the template's own stored copy", which is the same copy the editor
      is about to be filled with. So the pane draws once, immediately, and draws the right email.
    */
    const ownBlocks = blocksKey === selectedKey ? blocks : undefined;

    const requestId = previewRequestId.current + 1;
    previewRequestId.current = requestId;

    /*
      The campaign's fields go to the renderer only for the template that has them.

      Eight of the nine templates are fired by an account or billing event and have no campaign
      behind them — but the composer's `destination` and `detailRows` live on this page regardless,
      and they were sent with every preview. `welcome.ts` renders `{{__ctaUrl}}`, so previewing the
      welcome email drew its button pointing at whichever FanCam challenge had last been chosen for
      a campaign: a screen showing an operator a link that no welcome email will ever contain.

      Passing `undefined` restores what the endpoint documents as the no-campaign case — the
      template's own declared card and its own default destination.
    */
    const campaignFields = isCampaignTemplate
      ? { destination, destinationTarget, detailRows, sampleVariables: { eventDate, nextAction } }
      : {
          destination: undefined,
          destinationTarget: undefined,
          detailRows: undefined,
          sampleVariables: undefined
        };

    setPreviewLoading(true);
    const res = await emailApi.previewTemplate(
      selectedKey,
      ownBlocks,
      /*
        The campaign's Event Date and Next Action, as typed, so the rows that print them show in the
        pane. Undefined left both rows out of every preview, which read as the chips not working.
      */
      campaignFields.sampleVariables,
      campaignFields.destination,
      campaignFields.destinationTarget,
      // The rows as they stand in the composer, so the pane shows what is being typed rather than
      // what was last saved.
      campaignFields.detailRows,
      previewLocale
    );

    // Superseded while in flight: a newer request owns the pane now, and it is still loading.
    if (previewRequestId.current !== requestId) return;

    markLoad('preview', res !== false);
    setPreview(res?.data || {});
    setPreviewLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedKey,
    blocksKey,
    isCampaignTemplate,
    blocks,
    destination,
    destinationTarget,
    detailRows,
    eventDate,
    nextAction,
    previewLocale
  ]);

  useEffect(() => {
    if (!isAdmin) return;
    refreshPreview();
    /*
      `isCampaignTemplate` is a dependency, and it is the load-order half of the fix above.

      The catalog arrives after the first render, so a page opened straight at
      `?template=welcome` renders its first preview while this still reads `true` — and nothing
      else here would ever correct it: a destination change does not re-fire the preview, and the
      settle below only watches the blocks. Depending on it means the answer is re-rendered the
      moment the catalog says which template this is, rather than at the operator's next keystroke.
    */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    /*
      `blocksKey` is deliberately *not* a dependency.

      The render above already used the template's own stored copy, and the copy that lands a
      moment later is that same copy — so re-running here would buy an identical second render of
      every template an operator opens. Edits are covered by the settle below, which is what
      watches `blocks`.
    */
  }, [isAdmin, selectedKey, isCampaignTemplate, previewLocale]);

  /*
    The languages the banner is written in, read from the server rather than listed here.

    Fetched once: the set changes when someone approves a new translation, not while a campaign is
    being composed. A failed read leaves the list empty, which hides the switch — the preview then
    behaves exactly as it did before this existed, which is the right way for this to fail.
  */
  useEffect(() => {
    if (!isAdmin) return;

    let live = true;

    emailApi.getBannerCopy().then((res) => {
      if (live && res?.data?.locales) setPreviewLocales(res.data.locales);
    });

    return () => {
      live = false;
    };
  }, [isAdmin]);

  /*
    The preview follows the fields, having deliberately refused to.

    What stood here said it plainly: "re-rendering on every keystroke would be a request per
    character. The operator presses Refresh preview for their own edits." The objection was right
    and is answered rather than overruled — the cost it named was one request *per character*, and
    a settle is what makes that untrue. A burst of typing costs one render, not thirty.

    350ms because that is what the audience filter above already uses, for the same reason: a
    control that changes on every keystroke behind a request that is not free. One settle on the
    page is one thing to reason about; two would be a question about which.

    Deliberately not watching `destination` — `refreshPreview`'s own identity covers it, and the
    template effect above covers a template change, so a destination change is picked up by the next
    render without a second timer racing this one.
  */
  useEffect(() => {
    if (!isAdmin || (!dirty && !rowsDirty)) return undefined;

    const timer = setTimeout(() => refreshPreview(), 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, blocks, detailRows]);

  /*
    A width request from the editor's Preview buttons.

    Two effects rather than one, and both are wanted: the parameter so the choice persists, the
    count so the pane treats this as a request even when the parameter did not move.
  */
  const requestPreviewVariant = useCallback(
    (variant) => {
      updateParams({ variant });
      setPreviewVariantRequest((count) => count + 1);
    },
    [updateParams]
  );

  /*
    Every challenge, not the newest page.

    This asked once with no arguments, so it got the endpoint's default page: the 25 newest. Older
    challenges could not be chosen at all, and the only sign was "Showing the most recent
    challenges": QA's "not all FanCam Challenges are displayed". It now pages through the whole list
    at the endpoint's own maximum, and the picker filters what it holds. The ceiling only stops a
    runaway list holding the composer; `challengesHasMore` stays true if it is ever reached.
  */
  const loadChallenges = useCallback(async () => {
    setChallengesLoading(true);

    const all = [];
    let hasMore = true;
    let ok = true;
    for (let page = 0; hasMore && page < CHALLENGE_PAGES_MAX; page += 1) {
      // eslint-disable-next-line no-await-in-loop -- each page starts where the previous one ended
      const res = await emailApi.listFancamChallenges(undefined, all.length, CHALLENGE_PAGE_SIZE);
      if (res === false) {
        ok = false;
        break;
      }
      all.push(...(res?.data?.challenges || []));
      hasMore = !!res?.data?.hasMore;
    }

    markLoad('challenges', ok);
    setChallenges(all);
    setChallengesHasMore(hasMore);
    setChallengesLoading(false);
  }, [markLoad]);

  /*
    Not debounced: this runs when the destination becomes a FanCam, which is a single deliberate
    choice rather than something typed. The picker's search filters the loaded list in place.
  */
  useEffect(() => {
    if (!isAdmin || destination !== 'fancam') return undefined;

    loadChallenges();
    return undefined;
  }, [isAdmin, destination, loadChallenges]);

  /*
    Resolving the challenge a saved campaign already carries.

    Looked up by id rather than hunted for in the list, because a caption-ordered page need not
    contain it. A campaign whose challenge has been deleted is the case this exists for: the address
    still looks valid in the stored field and leads nowhere.
  */
  useEffect(() => {
    if (!isAdmin || destination !== 'fancam' || !destinationTarget) {
      setChallengeResolved(undefined);
      return;
    }

    let current = true;
    (async () => {
      const res = await emailApi.listFancamChallenges(
        undefined,
        undefined,
        undefined,
        destinationTarget
      );
      if (!current) return;
      setChallengeResolved(res?.data?.challenges?.[0] ?? null);
    })();

    return () => {
      current = false;
    };
  }, [isAdmin, destination, destinationTarget]);

  const loadReport = useCallback(
    async (id) => {
      setReportId(id || '');
      if (!id) return setReportStats(null);

      setReportLoading(true);
      const res = await emailApi.getCampaignStats(id);
      markLoad('reportStats', res !== false);
      setReportStats(res?.data || null);
      setReportLoading(false);
      return undefined;
    },
    [markLoad]
  );

  // --- Actions ---

  const saveText = async () => {
    begin('save-text');
    const saved = await emailApi.updateTemplateText(selectedKey, blocks, 'save-template');
    if (refused('save-text', saved)) return setRunning(null);

    const [fresh, history] = await Promise.all([
      emailApi.getTemplate(selectedKey),
      emailApi.listTemplateVersions(selectedKey)
    ]);
    setTemplate(fresh?.data || null);
    setVersions(history?.data || { versions: [], nextCursor: null });
    setDirty(false);
    setRunning(null);
    refreshPreview();
  };

  /*
    Guards unsaved template text.

    Any of Settings, Create Campaign or a nav click drops the `?template=` parameter, which changes
    `selectedKey` and re-runs the load effect — and that effect calls `setBlocks(...)` and
    `setDirty(false)`. So the "Unsaved changes" badge was on screen right up to the moment the work
    was thrown away, with nothing asked. Version restore did the same thing without asking.

    A confirm rather than a modal: it has to work from a navigation handler and from a browser
    unload, and both need a synchronous answer.
  */
  const confirmDiscard = useCallback(() => {
    if (!dirty) return true;

    return window.confirm(
      'This template has unsaved changes. Leaving now discards them. Continue?'
    );
  }, [dirty]);

  const restore = async (versionId) => {
    if (!confirmDiscard()) return;

    begin('restore');
    await emailApi.restoreTemplateVersion(versionId, 'restore-version');
    const [fresh, history] = await Promise.all([
      emailApi.getTemplate(selectedKey),
      emailApi.listTemplateVersions(selectedKey)
    ]);
    setTemplate(fresh?.data || null);
    setBlocks(fresh?.data?.blocks || {});
    setVersions(history?.data || { versions: [], nextCursor: null });
    setRunning(null);
    refreshPreview();
  };

  /*
    Saves a draft — updating the one that is loaded rather than making another.

    The button said "Update Draft" once a campaign was open — it now says "Save Changes" — and called
    `createCampaign`, because no update endpoint existed. An operator correcting a name got a second
    campaign, the original untouched, and `campaignId` silently repointed at the copy.
  */
  /**
   * The audience half of a campaign save, in the shape the server stores it.
   *
   * One place for it because create and update both send it, and the two disagreeing about which
   * mode a campaign is in would be a campaign that reads one way and sends another. `filters` goes
   * on every payload as it always has — the server empties it for a channel campaign, so sending
   * it costs nothing and removing it would change the saved-segment path.
   */
  const audiencePayload = () => {
    if (audienceMode === AUDIENCE_MODE.CHANNEL_SPECIFIC && channelId) {
      return {
        filters,
        audienceMode: AUDIENCE_MODE.CHANNEL_SPECIFIC,
        channelAudience: { channelId, relationshipScope, channelLabel: channelLabel || undefined }
      };
    }

    if (audienceMode === AUDIENCE_MODE.DIRECT_RECIPIENT && recipientId) {
      return {
        filters,
        audienceMode: AUDIENCE_MODE.DIRECT_RECIPIENT,
        directRecipient: {
          userId: recipientId,
          recipientLabel: recipientLabel || undefined
        }
      };
    }

    /*
      A mode chosen but nothing selected yet falls back to the saved segment.

      Unchanged from the channel path's behaviour, and worth stating: the server refuses a criteria
      mode with no criteria, so sending the half-made choice would turn Save Draft into an error an
      operator cannot act on. Saving as a saved-segment draft keeps their name and copy, which is
      what they were trying to keep.
    */
    return { filters, audienceMode: AUDIENCE_MODE.SAVED_SEGMENT };
  };

  /**
   * Saves the composer, and answers with why it could not, or null once it has saved.
   *
   * The send actions below read the answer: they save first, and must not go ahead on a refusal.
   */
  const saveDraft = async () => {
    if (campaignId) {
      begin('save-draft');
      const updated = await emailApi.updateCampaign(
        campaignId,
        // An empty selection is sent as null, which clears it — distinct from omitting the field,
        // which would leave whatever the campaign was chasing before.
        {
          name: campaignName,
          ...audiencePayload(),
          eventDate,
          nextAction,
          detailRows,
          conversionAction: conversionAction || null,
          destination: destination || null,
          destinationTarget: destinationTarget || null
        },
        'update-campaign'
      );
      const wasRefused = refused('save-draft', updated);
      if (!wasRefused) {
        setRowsDirty(false);
        await loadCampaigns();
      }
      setRunning(null);
      return wasRefused ? refusalOf(updated) : null;
    }

    await createDraft();
    return null;
  };

  /*
    Save what is on screen before sending it.

    Test Send, Schedule, Send Now and Repeat all send the campaign as stored, while the preview shows
    the composer as typed. So an Event Date, a Next Action, a detail row or a newly linked row an
    operator could see in the preview went out without it unless they had pressed Save first: QA's
    "Action and Event Date do not work" and "the row link does not work", on the send side.

    Only a draft is saved. A scheduled or sending campaign cannot be edited, and its send is what is
    stored by design. A refusal stops the send and is reported beside the control that was pressed,
    not beside Save Draft, where nobody pressing Send Now is looking.
  */
  const savedBeforeSending = async (action) => {
    const status = campaigns.find((campaign) => campaign.id === campaignId)?.status;
    if (status && status !== CAMPAIGN_STATUS.DRAFT) return true;

    const problem = await saveDraft();
    if (!problem) return true;

    const message = `Nothing was sent. Your changes could not be saved first: ${problem}`;
    if (action === 'test-send') setTestSendResult(message);
    else setRefusals((prev) => ({ ...prev, [action]: message }));

    return false;
  };

  /* Named 'save-draft' like the branch above it: it is the same button, so it wears the same label. */
  const createDraft = async () => {
    begin('save-draft');
    const res = await emailApi.createCampaign(
      /*
        `blocks` is no longer sent. The endpoint validated it and dropped it — a campaign's copy is
        a pinned template version, not a campaign field — so sending it advertised a capability
        that did not exist. Event date and next action ARE campaign fields, and are sent.
      */
      {
        name: campaignName,
        templateKey: selectedKey,
        ...audiencePayload(),
        eventDate,
        nextAction,
        /*
          Omitted on a campaign nobody has touched the section on, so the server seeds the four rows
          the template renders. Sending `[]` would say "this campaign has no card", which is a
          different answer and one no operator gave.
        */
        detailRows: detailRows.length > 0 ? detailRows : undefined,
        conversionAction: conversionAction || undefined,
        destination: destination || undefined,
        destinationTarget: destinationTarget || undefined
      },
      'create-campaign'
    );
    if (!refused('save-draft', res)) {
      if (res?.data?.id) setCampaignId(res.data.id);
      // The rows the server seeded, so the card section shows what this campaign will send rather
      // than staying empty until the page is reopened.
      if (res?.data?.detailRows) setDetailRows(res.data.detailRows);
      setRowsDirty(false);
      await loadCampaigns();
    }
    setRunning(null);
  };

  const schedule = async ({ scheduledAt, timezone }) => {
    if (!campaignId) return;
    if (!(await savedBeforeSending('schedule'))) return;
    begin('schedule');
    const res = await emailApi.scheduleCampaign(
      { campaignId, scheduledAt, timezone },
      'schedule-campaign'
    );
    if (refused('schedule', res)) return setRunning(null);

    await loadCampaigns();
    /*
      Confirmed here, because the queue this used to be read from is on the Dashboard.

      Scheduling refreshed the campaign list and the campaign list is not on this page, so a
      successful schedule looked exactly like a refused one from the composer.
    */
    setDone({
      action: 'schedule',
      message:
        'Scheduled. It is in the campaign list beside these controls, where it can be cancelled up to the moment it sends.'
    });
    setRunning(null);
  };

  const cancel = async (id) => {
    begin('cancel');
    await emailApi.cancelCampaign(id, 'cancel-campaign');
    await loadCampaigns();
    setRunning(null);
  };

  /**
   * "Send Now" — five minutes out, and still cancellable for those five minutes.
   *
   * The delay is surfaced rather than hidden: an operator who thinks they have sent instantly and
   * then spots a typo needs to know the window exists.
   */
  const sendNow = async () => {
    if (!campaignId) return;
    if (!(await savedBeforeSending('send-now'))) return;
    begin('send-now');

    /*
      The response is kept, not discarded.

      `sendCampaignNow` returns `cancellableUntil` and `cancellableForMinutes` — the window that is
      the only chance to stop a send already queued — and this threw them away. `emailApi`'s own
      comment said "the screen shows it"; it did not. So an operator was told a five-minute window
      existed and never told when it closed, with the control that uses it on another page.
    */
    const res = await emailApi.sendCampaignNow(campaignId, 'send-campaign-now');
    if (refused('send-now', res)) return setRunning(null);

    setSendWindow(
      res?.data?.cancellableUntil
        ? {
            campaignId,
            until: res.data.cancellableUntil,
            minutes: res.data.cancellableForMinutes
          }
        : null
    );

    await loadCampaigns();
    setRunning(null);
  };

  /**
   * One proof to the operator's own address.
   *
   * Silent, so the hourly-limit refusal can be rendered inline with its reset time. A bare toast
   * saying "too many requests" tells an operator nothing they can act on.
   */
  const testSend = async () => {
    if (!campaignId) return;
    setTestSendResult(null);
    if (!(await savedBeforeSending('test-send'))) return;
    begin('test-send');

    /*
      No blocks. The proof now renders the campaign as stored, which is what the real send renders.
      Passing the editor's in-memory copy made the proof a different document from the send.
    */
    const response = await emailApi.testSendCampaign(campaignId, 'test-send-campaign');

    if (response?.err) {
      setTestSendResult(
        response.resetsAt
          ? `${response.err} You can send another after ${new Date(response.resetsAt).toLocaleTimeString()}.`
          : response.err
      );
    } else {
      const remaining = response?.data?.remainingThisHour;
      setTestSendResult(
        `Test sent to ${response?.data?.sentTo}. ${remaining} more available this hour. It does not appear in this campaign's results.`
      );
    }

    setRunning(null);
  };

  /**
   * Turns the draft into a repeating series.
   *
   * `startAt` arrives as the same local wall-clock string the scheduler uses, so an operator sets a
   * repeat exactly the way they set a one-off — a date, a time and a zone — and the cadence decides
   * which of the two it becomes.
   */
  const setRecurrence = async (recurrence) => {
    if (!campaignId) return;
    if (!(await savedBeforeSending('set-recurrence'))) return;
    begin('set-recurrence');
    const res = await emailApi.setRecurrence({ campaignId, ...recurrence }, 'set-recurrence');
    if (refused('set-recurrence', res)) return setRunning(null);

    await loadCampaigns();
    setDone({
      action: 'set-recurrence',
      message:
        'Repeating. The campaign list shows its next sends, each send is listed with its own results once it goes out, and the whole series can be stopped from the campaign list.'
    });
    setRunning(null);
  };

  const stopRecurrence = async (id) => {
    begin('stop-recurrence');
    await emailApi.stopRecurrence(id, 'stop-recurrence');
    await loadCampaigns();
    setRunning(null);
  };

  const duplicate = async (id) => {
    begin('duplicate');
    const res = await emailApi.duplicateCampaign(id, undefined, 'duplicate-campaign');
    await loadCampaigns();
    if (res !== false) {
      setDone({
        action: 'duplicate',
        message:
          'Copied into a new draft. It is in the campaign list on the Dashboard — this composer is still editing the original.'
      });
    }
    setRunning(null);
  };

  /** Archiving hides a finished campaign. It never stops a send — cancel does that. */
  const setArchived = async (id, archived) => {
    begin('archive');
    const res = await emailApi.setCampaignArchived(id, archived, 'archive-campaign');
    await loadCampaigns();
    if (res !== false) {
      setDone({
        action: 'archive',
        message: archived
          ? 'Archived. It is hidden from the campaign list until the Archived filter is chosen, and its results are kept.'
          : 'Restored to the campaign list.'
      });
    }
    setRunning(null);
  };

  /**
   * Downloads the CSV.
   *
   * Fetched as a blob rather than navigating, because the export endpoint needs the Authorization
   * header the API wrapper attaches and a plain link would send none.
   */
  const exportCsv = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${emailApi.exportAudienceUrl}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await getToken()}`
          },
          body: JSON.stringify(filters)
        }
      );

      /*
        Checked before anything is written.

        `fetch` resolves on a 401, a 403 and a 500 alike, and the body was turned into a blob and
        downloaded regardless — so a refusal arrived on the operator's disk as
        `p4p-audience-2026-09-09.csv` containing an error document, named and dated exactly like a
        real export. That is worse than no file: it is a plausible-looking audience list that is
        not one, and nothing on the screen said so.
      */
      if (!response.ok) {
        setExportError(
          response.status === 401 || response.status === 403
            ? 'The export was refused — your session may have expired. Sign in again and retry.'
            : `The export failed (${response.status}). Nothing was downloaded.`
        );
        return;
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `p4p-audience-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();

      /*
        Revoked on the next turn of the loop, not on the line after `click()`.

        `click()` only queues the download; revoking the URL synchronously after it raced the
        browser reading from it, which on a large audience is when the download is most likely to
        be still starting. A timeout of zero is enough — it just has to be after the current task.
      */
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (error) {
      // A network failure resolves nothing, so without this the button simply stopped spinning.
      setExportError('The export could not be reached. Check your connection and retry.');
    } finally {
      setExporting(false);
    }
  };

  /**
   * Saves policy, merging rather than replacing.
   *
   * Two pages write to `EmailSettings` — Policy & Guardrails and the compliance footer on the
   * Analytics page — and the endpoint takes the whole document. Sending only the keys one page knows
   * about would blank the other's, so each save is merged onto what is already loaded.
   */
  const savePolicy = async (patch, action = 'save-policy') => {
    /*
      The caller names itself, because two panels share this function.

      Policy & Guardrails and the compliance footer both write `EmailSettings` through here, so a
      single action name would have put "Saving…" on both buttons whichever one was pressed —
      which is the confusion this whole change exists to remove.
    */
    begin(action);
    const res = await emailApi.updateFatiguePolicy(
      {
        fatigue: policy?.fatigue,
        segmentThresholds: policy?.segmentThresholds,
        billingNoticeLeadDays: policy?.billingNoticeLeadDays,
        complianceFooterText: policy?.complianceFooterText,
        ...patch
      },
      'save-policy'
    );
    if (refused(action, res)) return setRunning(null);

    if (res?.data) setPolicy({ ...res.data, persisted: true });
    setDone({
      action,
      message:
        action === 'save-compliance'
          ? 'Saved. Every email sent from now on carries this footer.'
          : 'Saved. It applies to every send evaluated from now on; sends already skipped are not revisited.'
    });
    setRunning(null);
  };

  /*
    Derives the country codes the measurement gate reads, on demand.

    A cron already keeps this clear, so this is for the two things a schedule cannot offer: doing it
    now, right after a deploy, and showing the operator which country names could not be resolved —
    a list that otherwise exists only in the server log, which is exactly what an operator without
    shell access cannot read.

    The unresolved names are held in state rather than folded into the confirmation string, because
    they are a list an operator acts on, not a sentence they acknowledge.
  */
  const [countryCodeResult, setCountryCodeResult] = useState(null);

  const runCountryCodeBackfill = async () => {
    begin('backfill-country-codes');
    setCountryCodeResult(null);

    const res = await emailApi.backfillCountryCodes('backfill-country-codes');
    if (refused('backfill-country-codes', res)) return setRunning(null);

    setCountryCodeResult(res?.data ?? null);
    setDone({
      action: 'backfill-country-codes',
      message:
        res?.data?.scanned > 0
          ? `Checked ${res.data.scanned} accounts and filled in ${res.data.updated}. Campaigns sent from now on can measure them.`
          : 'Nothing to do — every account with a country already has a code.'
    });
    setRunning(null);
  };

  /*
    The lookup records its own outcome, because the table below it could not tell three states apart.

    Never searched, searched and found nothing, and the request failed all rendered as no table and
    no text — so an operator answering "my verification email never arrived" saw the same blank
    panel whether the address had no sends or the query had errored. `markLoad` gives the three
    states: `undefined`, `true`, `false`.
  */
  const findSends = async () => {
    if (!lookup) return;
    begin('find-sends');
    const res = await emailApi.listSends({ recipientEmail: lookup, limit: 50 });
    markLoad('sends', res !== false);
    setSends(res?.data || { sends: [], nextCursor: null });
    setSearchedFor(res === false ? null : lookup);
    setRunning(null);
  };

  /**
   * A quick tag from the campaign builder, appended to the body.
   *
   * The editor's own chips insert at the caret; these are in a different section with no caret to
   * read, so they land at the end of the body text. Better than nothing and honest about it — the
   * editor above is the place to position one precisely.
   */
  /*
    `appendTag` is gone with the composer's chip row.

    It appended a variable label to `blocks.body`, which Compose had no editor for and nothing
    persisted. Insertion lives on the Templates editor, where it inserts at the caret.
  */

  const facets = useMemo(() => {
    /*
      Country and team come from the server's facets — the full domain.

      They used to be derived from whichever 25 rows were loaded, described here as "a sample, not
      the full domain". In practice that meant an operator could not filter to Japan unless a
      Japanese fan happened to be on the first page, and the pill rendered "None available" — a
      control that reads as broken while being merely uninformed. It made the brief's country and
      team segmentation silently incomplete.

      Subscription has no facet: its two choices are fixed (`SUBSCRIPTION_FILTER`), so they are
      listed by the filter itself. They used to be read off this page's rows, which held migrated
      old-platform tier names and nothing for any fan subscribed through today's plans.
    */
    const fromPage = (key) =>
      [...new Set(audience.rows.map((row) => row[key]).filter(Boolean))].sort();

    return {
      countries: audience.facets?.countries ?? fromPage('country'),
      teams: audience.facets?.teams ?? fromPage('team')
    };
  }, [audience.rows, audience.facets]);

  /*
    A repeating series counts: it is queued work, and its next send is on the list. It was left out
    because only a campaign has a send of its own, so a series set to go out today read "Nothing
    queued" beside it.
  */
  const queued = campaigns.filter(
    (campaign) =>
      [CAMPAIGN_STATUS.SCHEDULED, CAMPAIGN_STATUS.SENDING].includes(campaign.status) ||
      Boolean(campaign.recurrence?.active)
  ).length;

  if (!isAdmin) {
    return (
      <Shell>
        <Card>
          <Empty title="Administrator access required">
            The Email Manager is restricted to P4P administrators.
          </Empty>
        </Card>
      </Shell>
    );
  }

  const context = {
    // data
    templates,
    isCampaignTemplate,
    templatesLoaded,
    failed,
    loadOk,
    unseeded: templatesLoaded && templates.length === 0,
    template,
    blocks,
    dirty,
    versions,
    loadOlderVersions,
    detailRows,
    setDetailRows: changeDetailRows,
    rowsReadOnlyReason,
    conversionAction,
    setConversionAction,
    destination,
    setDestination,
    destinationTarget,
    setDestinationTarget,
    showVersions,
    filters,
    filtersWereSet,
    audienceMode,
    setAudienceMode,
    channelId,
    channelLabel,
    relationshipScope,
    setChannelAudience,
    channelAudience,
    channels,
    channelsLoading,
    recipientId,
    recipientLabel,
    recipientSearch,
    setRecipientSearch,
    setDirectRecipient,
    directRecipient,
    recipients,
    recipientsLoading,
    audience,
    audienceLoading,
    facets,
    events,
    preview,
    previewLoading,
    previewVariant,
    previewVariantRequest,
    previewLocale,
    previewLocales,
    setPreviewLocale,
    requestPreviewVariant,
    challenges,
    challengesLoading,
    challengesHasMore,
    challengeResolved,
    campaigns,
    campaignName,
    campaignId,
    queued,
    policy,
    loadPolicy,
    runCountryCodeBackfill,
    countryCodeResult,
    loadTemplates,
    busy,
    isRunning,
    confirmation,
    refusal,
    exporting,
    exportError,
    reportId,
    reportStats,
    reportLoading,
    lookup,
    sends,
    searchedFor,
    selectedKey,
    // setters
    setFilters,
    setBlocks,
    setDirty,
    setCampaignName,
    setLookup,
    updateParams,
    // actions
    loadAudience,
    loadCampaigns,
    refreshPreview,
    loadReport,
    saveText,
    restore,
    confirmDiscard,
    createDraft,
    saveDraft,
    schedule,
    cancel,
    sendNow,
    sendWindow,
    testSend,
    testSendResult,
    setRecurrence,
    stopRecurrence,
    duplicate,
    setArchived,
    exportCsv,
    savePolicy,
    findSends
  };

  return (
    <Shell>
      {/*
        The seeding notice lives here rather than on one page, because every page below depends on
        `EmailTemplate` having rows and all of them are equally empty without it.
      */}
      {/*
        Two banners, because these were one and should never have been.

        A failed fetch used to render the "no templates yet" notice — so a 500 told the operator
        their database was unseeded, which is a specific claim about a specific cause, and handed
        them a shell command to run. It also named a model and a script, neither of which means
        anything to the person reading it.

        The empty case is kept because it is real: an unseeded environment does exist. It now says
        what an operator can do about it rather than what an engineer would type.
      */}
      {failed('templates') ? (
        <Card>
          <Failed
            title="The email templates could not be loaded"
            onRetry={loadTemplates}
            busy={busy}
          >
            This is not an empty catalog — the request itself failed. The audience, campaign and
            policy pages work without it.
          </Failed>
        </Card>
      ) : templatesLoaded && templates.length === 0 ? (
        <Card>
          <Empty title="This environment has no email templates yet">
            Nothing has gone wrong: the template catalog is empty. It fills itself the first time
            the platform runs against a database, so this usually means that has not happened here
            yet. The audience, campaign and policy pages work without it.
          </Empty>
        </Card>
      ) : null}

      <Outlet context={context} />
    </Shell>
  );
};

export default EmailManager;
